import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, Plus, Upload, FileText, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function AdmissionsPage() {
  const [activeTab, setActiveTab] = useState<'list' | 'upload'>('list');

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-magenta-500" />
            Admissions
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Manage student admissions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild className="h-10 gap-2 text-xs sm:text-sm">
            <Link to="/admissions/upload">
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Upload Form</span>
              <span className="sm:hidden">Upload</span>
            </Link>
          </Button>
          <Button asChild className="h-10 gap-2 text-xs sm:text-sm">
            <Link to="/students/add">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Admission</span>
              <span className="sm:hidden">New</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-3 sm:p-5 rounded-2xl"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl gradient-magenta flex items-center justify-center mb-2 sm:mb-3">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-light-text dark:text-dark-text">Direct Entry</h3>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Add student manually with full details</p>
          <Link to="/students/add" className="inline-flex items-center gap-1 text-magenta-500 text-xs sm:text-sm mt-2 sm:mt-3 hover:underline">
            Get Started <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-3 sm:p-5 rounded-2xl"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl gradient-purple flex items-center justify-center mb-2 sm:mb-3">
            <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-light-text dark:text-dark-text">OCR Upload</h3>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Upload admission form for AI extraction</p>
          <Link to="/admissions/upload" className="inline-flex items-center gap-1 text-purple-500 text-xs sm:text-sm mt-2 sm:mt-3 hover:underline">
            Upload Form <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-3 sm:p-5 rounded-2xl col-span-2 sm:col-span-1"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl gradient-cyan flex items-center justify-center mb-2 sm:mb-3">
            <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-light-text dark:text-dark-text">Review OCR</h3>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Review and approve extracted forms</p>
          <Link to="/ocr-review" className="inline-flex items-center gap-1 text-cyan-500 text-xs sm:text-sm mt-2 sm:mt-3 hover:underline">
            Review Jobs <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
        </motion.div>
      </div>

      {/* Admission Process */}
      <div className="glass-card p-3 sm:p-6 rounded-2xl">
        <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4 sm:mb-6">Admission Process</h3>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          {[
            { step: '1', title: 'Fill Form', desc: 'Enter student details', color: 'gradient-purple' },
            { step: '2', title: 'Upload Docs', desc: 'Documents & photo', color: 'gradient-magenta' },
            { step: '3', title: 'Fee Payment', desc: 'Collect admission fee', color: 'gradient-gold' },
            { step: '4', title: 'Confirm', desc: 'Generate ID card', color: 'gradient-lime' },
          ].map((item, index) => (
            <div key={item.step} className="flex items-center gap-3 sm:gap-4 flex-1 w-full">
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl ${item.color} flex items-center justify-center text-white font-bold text-sm`}>
                  {item.step}
                </div>
                <div>
                  <p className="text-sm font-medium text-light-text dark:text-dark-text">{item.title}</p>
                  <p className="text-xs text-light-text-muted">{item.desc}</p>
                </div>
              </div>
              {index < 3 && (
                <div className="hidden sm:block w-6 lg:w-8 h-0.5 bg-light-border dark:bg-dark-border flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
